<template>
  <div v-if="visible" class="step-box gift-cards" :aria-busy="gift.busy">
    <div class="gift-heading gift-header">
      <span class="step-box-label">{{ stepNumber }}. Подарочные карты</span>
      <label><input type="checkbox" role="switch" aria-label="Использовать подарочные карты" :checked="gift.enabled" :disabled="submitting"
        @change="change({ enabled: $event.target.checked })" /></label>
    </div>
    <p v-if="gift.busy" role="status">Загружаем карты…</p>
    <div v-if="gift.error" role="alert">
      <p>{{ gift.error }}</p>
      <button type="button" :disabled="gift.busy || submitting" @click="load()">Повторить</button>
    </div>
    <template v-if="gift.ready">
      <p>Доступно карт: {{ gift.cards.length }}</p>
      <fieldset v-if="gift.enabled" :disabled="gift.busy || submitting || binding">
        <div v-for="payment in gift.selected" :key="payment.CardId" class="gift-card">
          <div class="gift-heading">
            <strong>{{ card(payment.CardId).Number }}</strong>
            <button type="button" :aria-label="'Убрать карту ' + card(payment.CardId).Number"
              @click="remove(payment.CardId)">×</button>
          </div>
          <p>Доступно {{ card(payment.CardId).CurrentBalance }} ₽
            <span v-if="card(payment.CardId).ExpiresAt"> · До {{ date(card(payment.CardId).ExpiresAt) }}</span>
          </p>
          <div class="gift-amount">
            <vue-slider :value="payment.AmountRubles" :min="0" :max="maximum(payment)" :interval="1"
              :disabled="maximum(payment) === 0 || submitting || binding" :tooltip="'none'"
              :dot-attrs="{ 'aria-label': 'Сумма списания с карты ' + card(payment.CardId).Number }" :use-keyboard="true"
              @change="setAmount(payment, $event)" />
            <label>Списать, ₽ <input type="number" min="0" :max="maximum(payment)" step="1"
              :value="payment.AmountRubles" @change="setAmount(payment, $event.target.value)" /></label>
          </div>
        </div>
        <div v-if="!menuOpen" class="gift-links">
          <button type="button" @click="open">Добавить карту</button>
        </div>
        <div v-else class="gift-form">
          <h3 class="gift-form-title">Выбор подарочной карты</h3>
          <button v-for="item in available" :key="item.CardId" type="button"
            class="gift-card-option" @click="add(item.CardId)">
            <strong>{{ item.Number }}</strong>
            <span>Доступно {{ item.CurrentBalance }} ₽<span v-if="item.ExpiresAt"> · До {{ date(item.ExpiresAt) }}</span></span>
          </button>
          <div v-if="available.length" class="gift-divider">ИЛИ</div>
          <label>Номер карты
            <input v-model="number" type="text" inputmode="numeric" maxlength="16" autocomplete="off" />
          </label>
          <button type="button" :disabled="!/^\d{16}$/.test(number)" @click="add()">Добавить</button>
          <button type="button" @click="menuOpen = false">Отменить</button>
        </div>
      </fieldset>
    </template>
    <p v-if="binding" role="status">Добавляем карту…</p>
    <p v-if="message" role="alert">{{ message }}</p>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component';
import { bindGiftCard } from '../services/giftCards';
import { giftCardError, rubles } from '../helpers/giftCards';

export default {
  components: { VueSlider },
  props: { initialized: Boolean, stepNumber: { type: Number, required: true } },
  data: () => ({ menuOpen: false, number: '', message: '', binding: false }),
  computed: {
    gift() { return this.$store.state.giftCards; },
    root() { return this.$store.state; },
    submitting() { return !!this.root.loading.createOrder; },
    eligible() { return this.root.user.isAuthorized && !this.root.isVkWidget && !this.root.isVkMobileWidget; },
    visible() { return this.$store.getters['giftCards/visible']; },
    context() {
      const { cart, user } = this.root;
      return JSON.stringify([this.$route.params.theatreId, this.$route.params.session, this.$route.query.filmId,
        user.isAuthorized, this.root.idToken, cart.tickets, cart.bar, cart.ticketSum, cart.barSum, cart.donations, this.root.promoData]);
    },
    available() { return this.gift.cards.filter(c => !this.gift.selected.some(p => p.CardId === c.CardId)); },
  },
  watch: {
    initialized(value) { if (value) this.load(); },
    context() { if (this.initialized) this.load(); },
    'root.user.paymentType'() { this.$store.dispatch('giftCards/recalculate'); },
  },
  methods: {
    async load(refresh = false) {
      if (!this.eligible) {
        this.$store.commit('giftCards/update', { ready: false, allowed: null, enabled: true, error: '', cards: [], selected: [], context: '', revision: this.gift.revision + 1, busy: false });
        return;
      }
      await this.$store.dispatch('giftCards/load', { theatreId: this.$route.params.theatreId,
        filmId: this.$route.query.filmId || this.root.movie.Id, context: this.context, refresh });
    },
    change(data) { return this.$store.dispatch('giftCards/change', data); },
    card(id) { return this.gift.cards.find(c => c.CardId === id) || {}; },
    date(value) { return new Date(value).toLocaleDateString('ru-RU'); },
    maximum(payment) {
      const other = this.gift.selected.reduce((sum, p) => sum + (p.CardId === payment.CardId ? 0 : p.AmountRubles), 0);
      return Math.min(rubles(this.card(payment.CardId).CurrentBalance),
        rubles(Number(this.root.cart.sum) + Number(this.root.cart.usedBonuses) - other));
    },
    setAmount(payment, value) {
      this.change({ selected: this.gift.selected.map(p => p.CardId === payment.CardId
        ? { ...p, AmountRubles: Math.min(rubles(value), this.maximum(payment)) } : p) });
    },
    remove(id) { this.change({ selected: this.gift.selected.filter(p => p.CardId !== id) }); },
    open() { this.menuOpen = true; this.number = ''; this.message = ''; },
    async add(id) {
      this.message = '';
      if (!id) {
        if (!/^\d{16}$/.test(this.number)) return;
        this.binding = true;
        try {
          const result = await bindGiftCard(this.number);
          id = result.CardId;
          await this.load(true);
          if (!this.gift.ready) return;
          if (!this.gift.cards.some(c => c.CardId === id)) {
            this.message = 'Карта добавлена в аккаунт, но недоступна для оплаты этого заказа.';
            this.menuOpen = false;
            return;
          }
        } catch (error) { this.message = giftCardError(error); return; }
        finally { this.binding = false; }
      }
      if (this.gift.cards.some(c => c.CardId === id) && !this.gift.selected.some(p => p.CardId === id)) {
        const payment = { CardId: id, AmountRubles: 0 };
        payment.AmountRubles = this.maximum(payment);
        await this.change({ selected: [...this.gift.selected, payment] });
      }
      this.menuOpen = false;
      this.number = '';
    },
  },
};
</script>

<style scoped>
.gift-heading, .gift-amount, .gift-links { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.gift-heading .step-box-label { margin: 0; }
.gift-header { justify-content: flex-start; flex-wrap: wrap; }
.gift-header label { display: inline-flex; align-items: center; gap: 8px; }
.gift-header input { margin: 0; }
.gift-cards fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
.gift-card { padding: 16px 0; border-bottom: 1px solid #777; }
.gift-card strong { overflow-wrap: anywhere; }
.gift-cards p { margin: 10px 0; }
.gift-amount > .vue-slider { flex: 1; min-width: 60px; }
.gift-amount input { width: 90px; }
.gift-cards input { padding: 8px; color: #111; background: #fff; border-radius: 4px; }
.gift-cards input[type=checkbox] { appearance: none; width: 38px; height: 22px; padding: 0; border: 1px solid #aaa; border-radius: 12px; background: #555; vertical-align: middle; position: relative; cursor: pointer; }
.gift-cards input[type=checkbox]::before { content: ''; position: absolute; width: 16px; height: 16px; border-radius: 50%; background: white; top: 2px; left: 2px; }
.gift-cards input[type=checkbox]:checked { background: #ec3025; }
.gift-cards input[type=checkbox]:checked::before { left: 18px; }
.gift-cards :focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
.gift-cards button { color: inherit; background: transparent; border: 1px solid #999; border-radius: 4px; padding: 8px; cursor: pointer; }
.gift-cards button:disabled { opacity: .5; cursor: default; }
.gift-links, .gift-form { margin-top: 16px; flex-wrap: wrap; }
.gift-form label { display: block; margin-bottom: 12px; }
.gift-form input { display: block; width: 100%; box-sizing: border-box; margin-top: 8px; }
.gift-form button { margin-right: 8px; }
.gift-form-title { margin: 0 0 12px; font-weight: 500; }
.gift-cards .gift-card-option { display: flex; flex-direction: column; gap: 6px; width: 100%; box-sizing: border-box; margin: 0 0 8px; padding: 12px; text-align: left; overflow-wrap: anywhere; background: rgba(255, 255, 255, .04); }
.gift-card-option:not(:disabled):hover { background: rgba(255, 255, 255, .1); }
.gift-card-option span { font-size: 14px; color: #bbb; }
.gift-divider { display: flex; align-items: center; gap: 12px; margin: 16px 0; color: #aaa; font-size: 12px; }
.gift-divider::before, .gift-divider::after { content: ''; flex: 1; height: 1px; background: #666; }
@media (max-width: 400px) { .gift-heading { flex-wrap: wrap; } }
</style>
